import { useEffect, useState } from "react";
import {
  FileText,
  Paperclip,
  Send,
  Sparkles,
  UserRound,
  X,
} from "lucide-react";
import type { ItMeta, ItPrioridad } from "../types/it";
import type { CreateItIncidentPayload } from "../api/it";

type Props = {
  open: boolean;
  meta: ItMeta;
  submitting: boolean;
  onClose: () => void;
  onSubmit: (payload: CreateItIncidentPayload) => Promise<void>;
};

const defaultForm = {
  titulo: "",
  descripcion: "",
  observaciones: "",
  prioridad: "media" as ItPrioridad,
  categoria: "",
  proyecto_id: "",
};

export function ItCreateIncidentModal({
  open,
  meta,
  submitting,
  onClose,
  onSubmit,
}: Props) {
  const [form, setForm] = useState(defaultForm);
  const [files, setFiles] = useState<File[]>([]);

  useEffect(() => {
    if (!open) return;

    setForm(defaultForm);
    setFiles([]);
  }, [open]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-slate-950/30 px-4 py-8 backdrop-blur-md">
      <div className="dashboard-scale-in relative max-h-[90vh] w-full max-w-5xl overflow-hidden rounded-[40px] border border-[#d7e5df] bg-white shadow-[0_44px_120px_rgba(15,23,42,.22)]">
        <button
          type="button"
          onClick={onClose}
          className="absolute right-5 top-5 z-20 grid h-11 w-11 place-items-center rounded-2xl border border-slate-200 bg-white text-slate-500 shadow-[0_8px_20px_rgba(15,23,42,.04)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-rose-50 hover:text-rose-600"
        >
          <X size={17} />
        </button>

        <div className="grid max-h-[90vh] overflow-y-auto lg:grid-cols-[1.05fr_.95fr]">
          <div className="relative overflow-hidden border-b border-[#dfe8e4] bg-[linear-gradient(135deg,#ffffff_0%,#f3fbf7_100%)] p-8 lg:border-b-0 lg:border-r">
            <div className="pointer-events-none absolute -right-24 top-[-120px] h-[280px] w-[280px] rounded-full bg-emerald-300/24 blur-3xl" />
            <div className="pointer-events-none absolute bottom-[-140px] left-[-90px] h-[260px] w-[260px] rounded-full bg-teal-300/14 blur-3xl" />
            <div className="pointer-events-none absolute inset-0 opacity-[0.22]">
              <div className="h-full w-full bg-[radial-gradient(rgba(15,23,42,.12)_0.6px,transparent_0.6px)] bg-[size:18px_18px]" />
            </div>

            <div className="relative">
              <div className="inline-flex items-center gap-2 rounded-full border border-emerald-100 bg-emerald-50/85 px-4 py-2 text-xs font-black uppercase tracking-[0.18em] text-emerald-700 shadow-[0_8px_22px_rgba(16,185,129,.08)]">
                <Sparkles size={14} />
                Nueva incidencia
              </div>

              <h3 className="mt-5 max-w-xl text-3xl font-black tracking-[-0.045em] text-slate-950 lg:text-4xl">
                Registra una incidencia IT con contexto completo
              </h3>

              <p className="mt-4 max-w-md text-sm font-semibold leading-7 text-slate-500">
                Mantiene el flujo de solicitud, aprobación, asignación y cierre,
                pero con una experiencia más clara para alta inicial y adjuntos
                múltiples.
              </p>

              <div className="mt-8 grid gap-4">
                <InfoLine
                  icon={UserRound}
                  label="Solicitante"
                  value={meta.current_user.nombre}
                />

                <InfoLine
                  icon={Send}
                  label="Correo"
                  value={meta.current_user.correo}
                />

                <InfoLine
                  icon={Sparkles}
                  label="Rol"
                  value={meta.current_user.rol}
                />

                <InfoLine
                  icon={Paperclip}
                  label="Adjuntos"
                  value={`${files.length} archivo(s)`}
                />
              </div>
            </div>
          </div>

          <form
            className="space-y-5 bg-white p-8"
            onSubmit={async (event) => {
              event.preventDefault();

              await onSubmit({
                titulo: form.titulo.trim(),
                descripcion: form.descripcion.trim(),
                observaciones: form.observaciones.trim(),
                prioridad: form.prioridad,
                categoria: form.categoria.trim(),
                proyecto_id: form.proyecto_id ? Number(form.proyecto_id) : null,
                attachments: files,
              });
            }}
          >
            <div>
              <div className="text-xs font-black uppercase tracking-[0.18em] text-emerald-600">
                Detalle operativo
              </div>

              <div className="mt-1 text-xl font-black tracking-tight text-slate-950">
                Información de la incidencia
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2">
              <label className="block md:col-span-2">
                <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                  Título
                </span>

                <input
                  value={form.titulo}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      titulo: event.target.value,
                    }))
                  }
                  className="field-control"
                  placeholder="Título de la incidencia"
                  required
                />
              </label>

              <label className="block md:col-span-2">
                <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                  Descripción
                </span>

                <textarea
                  value={form.descripcion}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      descripcion: event.target.value,
                    }))
                  }
                  className="field-control min-h-[140px]"
                  placeholder="Describe el problema, su contexto y el impacto."
                  required
                />
              </label>

              <label className="block md:col-span-2">
                <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                  Observaciones iniciales
                </span>

                <textarea
                  value={form.observaciones}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      observaciones: event.target.value,
                    }))
                  }
                  className="field-control min-h-[110px]"
                  placeholder="Observaciones opcionales para el equipo técnico."
                />
              </label>

              <label className="block">
                <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                  Prioridad
                </span>

                <select
                  value={form.prioridad}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      prioridad: event.target.value as ItPrioridad,
                    }))
                  }
                  className="field-control"
                >
                  <option value="baja">Prioridad baja</option>
                  <option value="media">Prioridad media</option>
                  <option value="alta">Prioridad alta</option>
                </select>
              </label>

              <label className="block">
                <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                  Categoría
                </span>

                <input
                  list="it-incident-categories"
                  value={form.categoria}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      categoria: event.target.value,
                    }))
                  }
                  className="field-control"
                  placeholder="Selecciona o escribe una categoría"
                  autoComplete="off"
                  required
                />
                <datalist id="it-incident-categories">
                  {meta.categories.map((category) => (
                    <option key={category} value={category} />
                  ))}
                </datalist>
                <span className="mt-2 block text-xs font-semibold text-slate-400">
                  Puedes seleccionar una categoría existente o escribir una nueva.
                </span>
              </label>

              <label className="block md:col-span-2">
                <span className="mb-2 block text-xs font-black uppercase tracking-[0.16em] text-slate-400">
                  Proyecto
                </span>

                <select
                  value={form.proyecto_id}
                  onChange={(event) =>
                    setForm((current) => ({
                      ...current,
                      proyecto_id: event.target.value,
                    }))
                  }
                  className="field-control"
                >
                  <option value="">Sin proyecto</option>
                  {meta.projects.map((project) => (
                    <option key={project.id} value={String(project.id)}>
                      {project.nombre}
                    </option>
                  ))}
                </select>
              </label>
            </div>

            <label className="group flex cursor-pointer flex-col gap-3 rounded-[30px] border border-dashed border-emerald-100 bg-emerald-50/55 p-5 shadow-[0_10px_26px_rgba(16,185,129,.055)] transition-all duration-300 hover:-translate-y-0.5 hover:border-emerald-200 hover:bg-emerald-50">
              <span className="inline-flex items-center gap-2 text-sm font-black text-emerald-700">
                <span className="grid h-10 w-10 place-items-center rounded-2xl border border-emerald-100 bg-white text-emerald-700 shadow-[0_8px_20px_rgba(16,185,129,.07)] transition-all duration-300 group-hover:-rotate-3 group-hover:scale-105">
                  <Paperclip size={16} />
                </span>
                Adjuntar archivos
              </span>

              <span className="text-sm font-semibold leading-6 text-slate-500">
                Imágenes, documentos o vídeos de apoyo. Máximo 8 archivos.
              </span>

              <input
                type="file"
                multiple
                accept=".jpg,.jpeg,.png,.pdf,.doc,.docx,.webp,.mp4,.avi,.mov,.webm"
                className="hidden"
                onChange={(event) => {
                  setFiles(Array.from(event.target.files ?? []));
                }}
              />

              {files.length > 0 && (
                <div className="flex flex-wrap gap-2">
                  {files.map((file) => (
                    <span
                      key={`${file.name}-${file.size}`}
                      className="inline-flex items-center gap-1 rounded-full border border-white bg-white px-3 py-1.5 text-xs font-black text-slate-600 shadow-[0_8px_20px_rgba(15,23,42,.045)]"
                    >
                      <FileText size={12} className="text-emerald-600" />
                      {file.name}
                    </span>
                  ))}
                </div>
              )}
            </label>

            <div className="flex flex-wrap justify-end gap-3 border-t border-[#edf1f3] pt-5">
              <button
                type="button"
                onClick={onClose}
                disabled={submitting}
                className="rounded-full border border-slate-200 bg-white px-5 py-3 text-sm font-black text-slate-600 shadow-[0_8px_20px_rgba(15,23,42,.04)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-slate-50 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-60"
              >
                Cancelar
              </button>

              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 rounded-full bg-[linear-gradient(135deg,#059669,#10b981,#14b8a6)] px-6 py-3 text-sm font-black text-white shadow-[0_18px_40px_rgba(16,185,129,.24)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_28px_60px_rgba(16,185,129,.30)] disabled:cursor-not-allowed disabled:opacity-60"
              >
                <Send size={16} />
                {submitting ? "Guardando..." : "Solicitar incidencia"}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function InfoLine({
  icon: Icon,
  label,
  value,
}: {
  icon: typeof UserRound;
  label: string;
  value: string;
}) {
  return (
    <div className="group flex items-center justify-between gap-4 rounded-2xl border border-[#dfe8e4] bg-white/85 px-4 py-3 shadow-[0_10px_26px_rgba(15,23,42,.055)] backdrop-blur-xl transition-all duration-300 hover:-translate-y-0.5 hover:border-emerald-200 hover:bg-white">
      <div className="flex items-center gap-3">
        <span className="grid h-10 w-10 shrink-0 place-items-center rounded-2xl border border-emerald-100 bg-emerald-50 text-emerald-700 transition-all duration-300 group-hover:-rotate-3 group-hover:scale-105">
          <Icon size={16} />
        </span>

        <div className="text-xs font-black uppercase tracking-[0.18em] text-slate-400">
          {label}
        </div>
      </div>

      <div className="min-w-0 truncate text-right text-sm font-black text-slate-800">
        {value}
      </div>
    </div>
  );
}
